/**
 * Payroll employee-line settlement math for Progear Finance 2.0, module 1.7+
 * (palgalehe import + töötajapõhine tasumine).
 *
 * The payroll month materializes ONE net-wage calendar entry; underneath it the
 * palgaleht breaks the fund into per-employee lines. This module is the ONE
 * definition of a line's paid / outstanding / status and of the month-level
 * derivations the PDF parser checks against (replit.md master rule: one truth
 * per number; L9: integer cents only — no floats, no bare arithmetic in
 * components). Every marked employee payment is mirrored 1:1 as a manual
 * `cash_flow_payment` on the month entry, so the CALENDAR state keeps falling
 * out of the existing entry-level settlement helpers (cashflow.ts) — these
 * helpers only answer the per-employee questions the settle pane asks.
 */

import { outstandingOf, settledAmountOf, type SettleableEntry } from './cashflow';

/** The settlement-relevant slice of one employee line, integer cents. */
export interface PayrollLineSettlement {
  /** The employee's net wage for the month (what must reach their account). */
  netCents: number;
  /** Sum of marked payments against this line (0 when none yet). */
  paidCents: number;
}

/** Per-line settlement state shown in the settle pane (UI labels Estonian). */
export type PayrollLineStatus = 'unpaid' | 'partial' | 'settled';

/**
 * Outstanding net wage for one line, integer cents = net − paid. Unclamped,
 * mirroring entry-level `outstandingOf`: an overpaid line reads negative so the
 * discrepancy SURFACES instead of silently vanishing.
 */
export function payrollLineOutstandingOf(line: PayrollLineSettlement): number {
  return line.netCents - line.paidCents;
}

/**
 * The canonical per-line status: `settled` once nothing is outstanding
 * (covers the trivial zero-net line and an overpayment), `unpaid` while no
 * cash has moved, otherwise `partial`.
 */
export function payrollLineStatusOf(line: PayrollLineSettlement): PayrollLineStatus {
  if (payrollLineOutstandingOf(line) <= 0) return 'settled';
  if (line.paidCents === 0) return 'unpaid';
  return 'partial';
}

/**
 * The most that may be recorded against a line right now, integer cents:
 * the line's own outstanding capped by the month ENTRY's outstanding (the
 * mirror event may never overdraw the calendar chip), floored at 0. The API
 * REJECTS amounts above this (approved design — reject, not clamp); the UI
 * prefills it as the remainder.
 */
export function payrollPaymentCapOf(
  line: PayrollLineSettlement,
  entryOutstandingCents: number,
): number {
  return Math.max(0, Math.min(payrollLineOutstandingOf(line), entryOutstandingCents));
}

/** Aggregate settlement across a month's employee lines, integer cents. */
export interface PayrollLinesTotals {
  /** Σ net wages of the lines. */
  netCents: number;
  /** Σ marked payments. */
  paidCents: number;
  /** net − paid (unclamped, like the per-line outstanding). */
  outstandingCents: number;
  /** How many lines are `settled`. */
  settledLines: number;
  /** Total number of lines. */
  lineCount: number;
}

/**
 * The ONE place a month's employee lines are summed (pane header, import
 * preview checksum, aggregate chip cue) — no component adds wages itself
 * (master rule; L9).
 */
export function sumPayrollLines(lines: PayrollLineSettlement[]): PayrollLinesTotals {
  let net = 0;
  let paid = 0;
  let settled = 0;
  for (const line of lines) {
    net += line.netCents;
    paid += line.paidCents;
    if (payrollLineStatusOf(line) === 'settled') settled += 1;
  }
  return {
    netCents: net,
    paidCents: paid,
    outstandingCents: net - paid,
    settledLines: settled,
    lineCount: lines.length,
  };
}

/**
 * Paid share of the month's net fund as a whole percent — the settle pane's
 * progress bar reads THIS, never its own division. Zero net fund = 0% (no
 * division by zero). A ratio, never money (L9); same convention as `paidPctOf`.
 */
export function payrollPaidPctOf(
  totals: Pick<PayrollLinesTotals, 'netCents' | 'paidCents'>,
): number {
  if (totals.netCents === 0) return 0;
  return Math.round((totals.paidCents / totals.netCents) * 100);
}

/**
 * Derive the month's NET fund from the two totals the palgaleht prints:
 * `Palgafond kokku` (gross wage fund) − `Maksud kokku` (taxes) = the net that
 * actually leaves the bank. The parser cross-checks Σ employee net lines
 * against this — a mismatch means the PDF was misread and the import must
 * surface a warning instead of importing silently-wrong money.
 */
export function payrollNetFundOf(wageFundCents: number, taxesCents: number): number {
  return wageFundCents - taxesCents;
}

/**
 * The month-level settlement of the ONE net-wage calendar entry
 * (`payroll:<M>:net`), integer cents — the payroll grid's status badge. Derived
 * from the SAME entry-level helpers the calendar/register read (cashflow.ts
 * `settledAmountOf` / `outstandingOf`), so a month's badge, the chip and the
 * register can never disagree (L14; master rule: one truth per number). Every
 * per-employee payment is mirrored 1:1 onto this entry, so its settled cash IS
 * the fund's paid total — no separate line summation needed here.
 */
export interface PayrollMonthSettlement {
  /** Cash + adjustments recognized against the net-wage entry, integer cents. */
  paidCents: number;
  /** net − settled (unclamped, like `outstandingOf`): an overpaid month reads
   *  negative so the discrepancy surfaces instead of silently vanishing. */
  outstandingCents: number;
  /** `settled` once nothing is outstanding, `unpaid` while nothing is settled,
   *  otherwise `partial`. */
  status: PayrollLineStatus;
}

/**
 * Resolve a payroll net-wage entry's month-level settlement. `entry` is any
 * {@link SettleableEntry} (the API feeds the `cash_flow_day` view row); the
 * status mirrors {@link payrollLineStatusOf} so per-line and per-month states
 * use one rule.
 */
export function payrollMonthSettlementOf(entry: SettleableEntry): PayrollMonthSettlement {
  const outstandingCents = outstandingOf(entry);
  const paidCents = settledAmountOf(entry);
  const status: PayrollLineStatus =
    outstandingCents <= 0 ? 'settled' : paidCents === 0 ? 'unpaid' : 'partial';
  return { paidCents, outstandingCents, status };
}
